/**
 * Le 422 `GED_METADATA_TENDER_ID_RESERVED`, lu par son CODE.
 *
 * ## 🔴 E6, mesuré le 2026-09-19 (soir) : zéro occurrence dans tout l'écran
 *
 * `grep -r GED_METADATA_TENDER_ID_RESERVED frontend/src` (locales comprises)
 * rendait **0**. Le refus sort de `_ged_files_helpers._garder_la_portee_du_patch`
 * (l.118-124) avec sa phrase FRANÇAISE en dur, et `erreurDuCoffre` —
 * `phraseDuRefusDAppelDOffres` — rendait `null` sur ce code : le toast retombait
 * donc sur le message du serveur, français dans l'interface anglaise. Même
 * défaut, même jour, que `GED_FILE_LEGAL_RETENTION` (B4) ; même remède.
 *
 * ## Pourquoi une clé `ged.refus.*` et non `sales.tenders.documents.*`
 *
 * Ce refus ne parle d'AUCUN geste de consultation : il refuse d'ÉCRIRE la clé
 * `metadata.tender_id` depuis le coffre. Personne ne « déplace », « supprime »
 * ni « remplace » ici — on tente de poser, changer ou retirer un rattachement
 * qui ne se décide pas depuis cet écran. Le ranger sous la branche des gestes
 * d'appel d'offres aurait fait croire au lecteur suivant qu'il s'interpole avec
 * un verbe, comme `ownedByTender`. Même arbitrage que `ged.refus.legal_retention`.
 *
 * ⚠️ Le serveur n'envoie **aucune référence de consultation** avec ce refus
 * (`detail` porte `code`, `message` et `key: "tender_id"`, rien d'autre) : la
 * phrase n'en cite donc pas, et il n'y a pas de variante « NoReference » à
 * écrire. Une phrase qui nommerait une consultation ici serait inventée.
 *
 * ⚠️ La clé est écrite EN ENTIER, DANS l'appel à `t` : une clé construite
 * échapperait à tout extracteur, et une clé absente des deux catalogues laisse
 * la parité parfaitement verte.
 */

import type { TFunction } from 'i18next'

/** 422 — `metadata.tender_id` ne se pose, ne se change ni ne se retire au coffre. */
export const GED_METADATA_TENDER_ID_RESERVED = 'GED_METADATA_TENDER_ID_RESERVED'

/** `t` rend `undefined` tant qu'i18next n'est pas initialisé : jamais ça en toast. */
function texte(valeur: unknown): string | null {
    return typeof valeur === 'string' && valeur.trim() ? valeur : null
}

/**
 * La phrase du rattachement réservé, dans la langue de l'écran, ou `null`.
 *
 * `null` quand rien n'est traduit : l'appelant retombe alors sur le message du
 * serveur, qui reste juste — l'ordre des replis est le contrat d'`erreurLue`.
 */
export function phraseDuRattachementReserve(t: TFunction): string | null {
    return texte(
        t('ged.refus.tender_id_reserved', {
            defaultValue:
                "Le rattachement d'un document à un appel d'offres ne se pose, ne se change ni ne se retire ici. Déposez le document depuis la fiche de l'appel d'offres, onglet Documents.",
        }),
    )
}
